import { VisitRequestTableRowFragment } from '@/components/bulkQueue/tables/queries.gql.types';
import { DateRecommendationFragment } from '@/components/decisionSupport/queries/dateRecommendations.gql.types';
import { PatientTags } from '@/components/patients/PatientTags';
import { NullableFormattedDate } from '@/utils/datetimes/formatting';
import { ThumbUpAlt } from '@mui/icons-material';
import { Typography } from '@mui/material';
import moment from 'moment';

import { IBasicAddress, addressInOneLine, basicAddressToAddress } from '../../../utils/addresses';
import { AxleIconButton } from '../../buttons/AxleIconButton';
import { ClinicianChip, IClinicianChip } from '../../clinicians/ClinicianChip';
import { CaptionLabel } from '../../labels/CaptionLabel';
import { NoneLabel } from '../../labels/NoneLabel';
import { ServiceStatusTag } from '../../services/ServiceStatusTag';
import { HorizontalStack, VerticalStack } from '../../utils/linearLayouts';
import { useUpdateTentativeDate } from '../mutations/updateTentativeDate';

// Hoisted so re-renders reuse the same sx reference (no Emotion re-serialization).
const SPACE_BETWEEN_SX = { justifyContent: 'space-between' };

export function VisitRequestPatientCell({
    visitRequest,
    showTags = false,
}: {
    visitRequest: {
        patient: {
            fullName: string;
            tags?: { items: { id: string; name: string }[] } | null;
        };
        address: IBasicAddress | null;
    };
    showTags?: boolean;
}) {
    return (
        <VerticalStack>
            <Typography fontSize={'14px'}>{visitRequest.patient.fullName}</Typography>
            {showTags && <PatientTags tags={visitRequest.patient.tags?.items ?? []} fullWidth />}
            {visitRequest.address && (
                <Typography fontSize={'0.8em'}>
                    {addressInOneLine(basicAddressToAddress(visitRequest.address))}
                </Typography>
            )}
        </VerticalStack>
    );
}

export function ServiceDisciplinesLabel({
    disciplines,
}: {
    disciplines: VisitRequestTableRowFragment['disciplines'];
}) {
    return (
        <CaptionLabel fontSize={'0.8em'}>
            {disciplines.length > 0
                ? disciplines.map((discipline) => discipline.displayName).join(', ')
                : 'Services'}
        </CaptionLabel>
    );
}

export const AppointmentServicesCell = ({
    appointments,
}: {
    appointments: {
        id: string;
        services: {
            id: string;
            name: string;
            isActive?: boolean;
        }[];
    }[];
}) => {
    return (
        <VerticalStack>
            {appointments.length > 0 ? (
                appointments.map((appointment) => (
                    <VerticalStack key={appointment.id}>
                        {appointment.services.length > 0 ? (
                            appointment.services.map((service) => (
                                <HorizontalStack key={service.id} spacing={1} alignItems='center'>
                                    <Typography fontSize={'14px'}>{service.name}</Typography>
                                    <ServiceStatusTag isActive={service.isActive} />
                                </HorizontalStack>
                            ))
                        ) : (
                            <NoneLabel text='No services' fontSize={'14px'} />
                        )}
                    </VerticalStack>
                ))
            ) : (
                <NoneLabel text='No services' fontSize={'14px'} />
            )}
        </VerticalStack>
    );
};

export const AppointmentDisciplineServicesCell = ({
    disciplines,
    appointments,
}: {
    disciplines: VisitRequestTableRowFragment['disciplines'];
    appointments: {
        id: string;
        services: {
            id: string;
            name: string;
            isActive?: boolean;
        }[];
    }[];
}) => {
    return (
        <VerticalStack>
            <ServiceDisciplinesLabel disciplines={disciplines} />
            {appointments.length > 0 ? (
                appointments.map((appointment) => (
                    <VerticalStack key={appointment.id}>
                        {appointment.services.length > 0 ? (
                            appointment.services.map((service) => (
                                <HorizontalStack key={service.id} spacing={1} alignItems='center'>
                                    <Typography fontSize={'14px'}>{service.name}</Typography>
                                    <ServiceStatusTag isActive={service.isActive} />
                                </HorizontalStack>
                            ))
                        ) : (
                            <NoneLabel text='No services' fontSize={'14px'} />
                        )}
                    </VerticalStack>
                ))
            ) : (
                <NoneLabel text='No services' fontSize={'14px'} />
            )}
        </VerticalStack>
    );
};

export const AppointmentClinicianRow = ({
    appointment: { clinician },
}: {
    appointment: {
        clinician: IClinicianChip | null;
    };
}) => (
    <HorizontalStack>
        {clinician ? (
            <ClinicianChip clinician={clinician} showLink />
        ) : (
            <NoneLabel text='Unassigned' fontSize={'14px'} />
        )}
    </HorizontalStack>
);

export const AppointmentClinicianCell = ({
    appointments,
}: {
    appointments: {
        id: string;
        clinician: IClinicianChip | null;
    }[];
}) => {
    return (
        <VerticalStack>
            {appointments.length > 0 ? (
                appointments.map((appointment) => (
                    <AppointmentClinicianRow key={appointment.id} appointment={appointment} />
                ))
            ) : (
                <NoneLabel text='Unassigned' fontSize={'14px'} />
            )}
        </VerticalStack>
    );
};

export const FullAppointmentCell = ({
    disciplines,
    appointments,
}: {
    disciplines: {
        id: string;
        displayName: string;
    }[];
    appointments: {
        id: string;
        clinician: IClinicianChip | null;
        services: {
            id: string;
            name: string;
            isActive?: boolean;
        }[];
    }[];
}) => {
    const label =
        disciplines.length > 0
            ? disciplines.map((discipline) => discipline.displayName).join(', ')
            : 'Services';
    return (
        <VerticalStack spacing={1}>
            <CaptionLabel fontSize={'0.8em'}>{label}</CaptionLabel>
            <HorizontalStack sx={SPACE_BETWEEN_SX}>
                <AppointmentServicesCell appointments={appointments} />
                <AppointmentClinicianCell appointments={appointments} />
            </HorizontalStack>
        </VerticalStack>
    );
};

export const VisitRequestTentativeDateCell = ({
    visitRequestId,
    tentativeDate,
    dateRecommendations,
}: {
    visitRequestId: string;
    tentativeDate: Date | null;
    dateRecommendations?: DateRecommendationFragment[];
}) => {
    const { updateTentativeDate } = useUpdateTentativeDate({
        visitRequestId,
    });
    const recommendedDate = dateRecommendations?.[0]?.date ?? null;
    return tentativeDate ? (
        <NullableFormattedDate date={moment(tentativeDate)} />
    ) : recommendedDate ? (
        <HorizontalStack spacing={1}>
            <NullableFormattedDate date={moment(recommendedDate)} />
            <AxleIconButton
                color='primary'
                icon={<ThumbUpAlt />}
                onClick={(event) => {
                    event.stopPropagation();
                    void updateTentativeDate({
                        tentativeDate: recommendedDate,
                    });
                }}
                tooltip={`Plan for ${moment(recommendedDate).format('MM/DD/YYYY')}`}
            />
        </HorizontalStack>
    ) : (
        <NoneLabel fontSize={'14px'} />
    );
};
